import React from 'react';
import Layout from '../layout/Layout';
import CtaBand from '../layout/CtaBand';
import Section from '../ui/Section';
import PageHero from '../ui/PageHero';
import Button from '../ui/Button';
import Sparkle from '../ui/Sparkle';
import Eyebrow from '../ui/Eyebrow';
import Reveal from '../ui/Reveal';
import { APP } from '../layout/siteData';

const TEAM = [
  { name: 'Chris Edington', role: 'Chief Executive Officer', photo: '/marketing/team/chris-edington.png' },
  { name: 'Mike Joubert', role: 'Chairman', photo: '/marketing/team/mike-joubert.png' },
  { name: 'Elizora Yarnell', role: 'Chief Operations Officer', photo: '/marketing/team/elizora-yarnell.jpg' },
  { name: 'Bronwyn Furno', role: 'Head of Studio', photo: '/marketing/team/bronwyn-furno.png' },
  { name: 'Hannah Furno', role: 'Head of Customer Success', photo: '/marketing/team/hannah-furno.jpg' },
  { name: 'Dylan Klichowicz', role: 'Digital Marketing Manager', photo: '/marketing/team/dylan-klichowicz.jpg' },
];

const REASONS = [
  { title: 'Get selling, fast and easy.', text: 'Launch your online store with our easy-to-use, do-it-yourself tools — they’ll get you up and running in no time.', cta: { label: 'Try it for free', href: APP.signUp }, tint: 'bg-sky' },
  { title: 'Sell anything. Anywhere.', text: 'We’ve made selling on social easy by letting you synchronise your store with Google, Instagram and Facebook.', cta: { label: 'See our features', href: '/pricing-and-features' }, tint: 'bg-cream' },
  { title: 'Simple, transparent pricing.', text: 'Shopstar works month-to-month. There’s no long-term contract, so you can cancel whenever you like.', cta: { label: 'See our pricing', href: '/pricing-and-features' }, tint: 'bg-sky' },
  { title: 'Real, local support.', text: 'Our 100% South African Customer Success Team is with you from day one — by email, WhatsApp and live chat.', cta: { label: 'Talk to us', href: '/contact' }, tint: 'bg-cream' },
];

const MISSION = [
  ['Cape Town', 'Proudly built in South Africa'],
  ['100%', 'Local Customer Success Team'],
  ['Month-to-month', 'No long-term contracts'],
  ['Thousands', 'Of makers selling online'],
];

const ArrowLink = ({ href, children }) => (
  <a href={href} className="group inline-flex items-center gap-2 font-bold text-ink hover:text-amber transition-colors">
    {children}
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="transition-transform group-hover:translate-x-1"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
  </a>
);

const About = () => (
  <Layout>
    <PageHero
      eyebrow="About us"
      title="Our business is making yours a success."
      lead="We empower creative entrepreneurs through simple e-commerce that helps them start, manage and grow their online businesses. We exist to inspire people just like you to realise their dreams of building a successful online business."
    >
      <Button href={APP.signUp}>Start selling now</Button>
      <Button href="/pricing-and-features" variant="secondary" icon={false}>See pricing</Button>
    </PageHero>

    <Section tone="white" pad="md">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {MISSION.map(([v, l]) => (
          <div key={l} className="text-center bg-cream rounded-2xl border border-line py-7 px-4">
            <strong className="block text-2xl md:text-3xl font-extrabold text-ink tracking-tight">{v}</strong>
            <span className="block mt-2 font-semibold text-body text-sm">{l}</span>
          </div>
        ))}
      </div>
    </Section>

    <Section tone="cream" pad="lg">
      <div className="max-w-[40ch] mb-12">
        <Eyebrow>The people behind it</Eyebrow>
        <h2 className="mt-4 text-h2 font-extrabold text-ink">Meet our awesome team.</h2>
        <p className="mt-3 text-lead text-body">A small, dedicated South African team obsessed with your success.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {TEAM.map((m, i) => (
          <Reveal key={m.name} delay={(i % 3) * 70} className="bg-white rounded-3xl border border-line overflow-hidden text-center hover:-translate-y-1 hover:shadow-soft transition-all">
            <div className="relative aspect-square bg-sky">
              <img src={m.photo} alt={m.name} loading="lazy" className="w-full h-full object-cover object-top" />
              <Sparkle size={22} color="#95a8e6" className="absolute bottom-3 right-3 opacity-60" />
            </div>
            <div className="p-6">
              <strong className="block text-lg text-ink">{m.name}</strong>
              <span className="block text-muted text-[0.92rem] mt-1">{m.role}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>

    <Section tone="white" pad="lg">
      <div className="max-w-[40ch] mb-12"><Eyebrow>Why Shopstar</Eyebrow><h2 className="mt-4 text-h2 font-extrabold text-ink">Why choose Shopstar?</h2></div>
      <div className="grid md:grid-cols-2 gap-5">
        {REASONS.map((r, i) => (
          <Reveal key={r.title} delay={(i % 2) * 80} className={`${r.tint} rounded-4xl p-9`}>
            <Sparkle size={24} color="#95a8e6" />
            <h3 className="mt-4 text-h3 font-extrabold text-ink">{r.title}</h3>
            <p className="mt-3 text-body max-w-[46ch]">{r.text}</p>
            <div className="mt-5"><ArrowLink href={r.cta.href}>{r.cta.label}</ArrowLink></div>
          </Reveal>
        ))}
      </div>
    </Section>

    <CtaBand title="Start your online business today." />
  </Layout>
);

export default About;
